import { cloneElement, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import {
  Form,
  Input,
  Stack,
  Table,
  TableColumn,
  TableNoRecords,
  TableProps,
} from '@servicetitan/design-system';
import { BlockedActivityTooltip } from './BlockedActivityTooltip';
import { useSnapshot } from 'valtio';
import { serviceState } from '@modules/ServiceModule/workspace/lib/state';
import { ServiceUserUtils } from '@modules/ServiceModule/workspace/lib';
import { useEmployeeTableState } from './useEmployeeTableState';
import { useIsUserBlockedByActivity } from './useIsUserBlockedByActivity';
import { getConsolidatedStatus } from '@modules/UserStatusModule/utilities/ConsolidatedStatus/ConsolidatedStatus';
import { ConsolidatedUserStatusIndicator } from '@components/ConsolidatedUserStatusIndicator';
import { sanitizePhoneNumberTo10Digits } from '@lib/utils/formatPhone/sanitizePhoneNumberTo10Digits';
import { formatToNorthAmericanNumber } from '@lib/utils/formatPhone/formatToNorthAmericanNumber';
import ErrorComponent from '@components/ErrorComponent';
import styles from './OutboundModalEmployeeTable.module.css';
import strings from './strings';
import { mainLayoutLocators } from '@locators/main-layout';
import { inboxLocators } from '@locators/inbox';
import { OutboundCallEntity } from '@lib/types/common-call-types';
import { OutboundCallDetails } from '../../types';
import { initialCallDetails } from '../../context/contants';

const PhoneNumberCell: React.FC<{ phone: string }> = ({ phone }) => {
  if (!phone) {
    return <td>{strings.cells.unassignedPhoneNumber}</td>;
  }
  return (
    <td>{formatToNorthAmericanNumber(sanitizePhoneNumberTo10Digits(phone))}</td>
  );
};

// Direct useSnapshot(serviceState) is intentional here — useService() creates a new
// sessions array via Object.values() and runs isUserAvailable() on every render.
// Since this component renders once per row, we only subscribe to userById changes.
const StatusCell: React.FC<{ userId: string }> = ({ userId }) => {
  const { userById } = useSnapshot(serviceState);
  const serviceUser = userById[userId];

  if (!serviceUser) {
    return <td />;
  }

  return (
    <td>
      <ConsolidatedUserStatusIndicator
        consolidatedStatus={getConsolidatedStatus(serviceUser)}
        hasOfferedCall={ServiceUserUtils.isCallOffered(serviceUser)}
      />
    </td>
  );
};

interface EmployeeTableProps {
  setCallDetails: (details: OutboundCallDetails) => void;
}

const StatusCellRenderer: React.FC<{ dataItem: { id: string } }> = ({
  dataItem,
}) => <StatusCell userId={dataItem.id} />;

/**
 * Wraps Kendo's row element so we can call `useIsUserBlockedByActivity` per
 * row. Disabled rows get the DisabledRow greyout style; the explanatory
 * tooltip is rendered separately on the radio cell via BlockedActivityTooltip.
 */
const EmployeeRow: React.FC<{
  trElement: React.ReactElement<{ className?: string }>;
  userId: string;
  hasPhone: boolean;
}> = ({ trElement, userId, hasPhone }) => {
  const isBlockedByActivity = useIsUserBlockedByActivity(userId);
  const shouldDisable = !hasPhone || isBlockedByActivity;
  if (!shouldDisable) return trElement;
  return cloneElement(trElement, {
    ...trElement.props,
    className: classNames(trElement.props.className, styles.DisabledRow),
  });
};

const employeeRowRender: TableProps['rowRender'] = (trElement, props) => (
  <EmployeeRow
    trElement={trElement}
    userId={props.dataItem.id}
    hasPhone={!!props.dataItem.phone}
  />
);

interface EmployeeRowData {
  id: string;
  name: string;
  phone: string;
}

/** Per-row radio cell that subscribes to its own presence via the hook. */
const SelectableEmployeeCell: React.FC<{
  dataItem: EmployeeRowData;
  selectedId: string;
  onSelect: (row: EmployeeRowData) => void;
  onClearIfSelected: (id: string) => void;
}> = ({ dataItem, selectedId, onSelect, onClearIfSelected }) => {
  const isBlockedByActivity = useIsUserBlockedByActivity(dataItem.id);
  const isSelected = dataItem.id === selectedId;

  // If this row is currently selected and transitions to blocked (the target
  // started a call after the agent picked them), clear the selection so the
  // outer Call button cannot fire against a now-busy user.
  useEffect(() => {
    if (isSelected && isBlockedByActivity) {
      onClearIfSelected(dataItem.id);
    }
  }, [isSelected, isBlockedByActivity, dataItem.id, onClearIfSelected]);

  return (
    <td>
      <BlockedActivityTooltip blocked={isBlockedByActivity}>
        <Form.Radio
          id={`${mainLayoutLocators.outboundAndTransferModal.employeeRowRadioBtn}`}
          checked={isSelected}
          label=''
          onClick={() => onSelect(dataItem)}
          disabled={!dataItem.phone || isBlockedByActivity}
        />
      </BlockedActivityTooltip>
    </td>
  );
};

/**
 * OutboundModalEmployeeTable Component
 *
 * This component renders a table of employees that can be searched and selected.
 * Once the user selects an employee, the phone number of the selected employee is set using the `setNumber` function.
 * It includes a search input, a table with pagination, and various columns such as Agent, Status, and Phone Number.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {Function} props.setNumber - A function to set the phone number of the selected employee.
 *
 * @returns {JSX.Element} A stack containing a search input and a table of employees.
 *
 * Example usage:
 * ```tsx
 * <OutboundModalEmployeeTable setNumber={setNumberFunction} />
 * ```
 *
 */
const OutboundModalEmployeeTable: React.FC<EmployeeTableProps> = ({
  setCallDetails,
}) => {
  const state = useEmployeeTableState();
  const { onSelectionChange, selectedId } = state;

  const handleSelect = useCallback(
    (row: EmployeeRowData) => {
      onSelectionChange(row.id);
      setCallDetails({
        number: sanitizePhoneNumberTo10Digits(row.phone),
        callDisplayName: row.name,
        agentId: row.id,
        entity: OutboundCallEntity.Employees,
      });
    },
    [onSelectionChange, setCallDetails],
  );

  const handleClearIfSelected = useCallback(
    (id: string) => {
      if (selectedId !== id) return;
      onSelectionChange('');
      setCallDetails(initialCallDetails);
    },
    [selectedId, onSelectionChange, setCallDetails],
  );

  const renderSelectableCell = useCallback(
    (props: { dataItem: EmployeeRowData }) => (
      <SelectableEmployeeCell
        dataItem={props.dataItem}
        selectedId={selectedId}
        onSelect={handleSelect}
        onClearIfSelected={handleClearIfSelected}
      />
    ),
    [selectedId, handleSelect, handleClearIfSelected],
  );

  return (
    <Stack direction='column' spacing={2}>
      <Stack.Item>
        <Input
          id={inboxLocators.callBar.searchInputUser}
          icon={{ name: 'search' }}
          iconPosition='left'
          className='w-100'
          size='small'
          placeholder='Search for agent name or number'
          onChange={state.onSearchChange}
          value={state.search}
        />
      </Stack.Item>

      <Stack.Item>
        <Table
          selectable={{ enabled: true, mode: 'single' }}
          data={state.data}
          loading={state.loading}
          pageable={{ buttonCount: 3 }}
          pageSize={state.page.take}
          skip={state.page.skip}
          total={state.total}
          onPageChange={state.onPageChange}
          className={styles.EmployeeTable}
          rowHeight={40}
          id={inboxLocators.callBar.usersListTableToCall}
          rowRender={employeeRowRender}
        >
          <TableNoRecords>
            <div className='ta-center m-x-auto p-y-6' style={{ width: 420 }}>
              <ErrorComponent
                image='block'
                title='No results found'
                body='Try another search.'
              />
            </div>
          </TableNoRecords>
          <TableColumn width={40} cell={renderSelectableCell} />
          <TableColumn title={strings.header.employee} field='name' />
          <TableColumn
            width={200}
            title={strings.header.status}
            field='status'
            cell={StatusCellRenderer}
          />
          <TableColumn
            title={strings.header.phoneNumber}
            field='phone'
            cell={(props) => PhoneNumberCell({ phone: props.dataItem.phone })}
          />
        </Table>
      </Stack.Item>
    </Stack>
  );
};

export default OutboundModalEmployeeTable;
